import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel.ts";
import { TransformedSubConfiguratorOrListComponent } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";

export function findFirstCompatibleSubConfiguratorComponent(
	targetComponents: TransformedSubConfiguratorOrListComponent[],
	sourceItem: ConfiguratorTreeItemModel,
): TransformedSubConfiguratorOrListComponent | undefined {
	if (sourceItem.configuratorType.type !== "subConfigurator") return undefined;
	const sourceCatalogPartId = sourceItem.configuratorType.subConfiguratorCatalogPartId;

	return targetComponents.find((component) => component.catalogPartId === sourceCatalogPartId);
}

export interface SubConfigurationImportAssignment {
	sourceItem: ConfiguratorTreeItemModel;
	targetComponent: TransformedSubConfiguratorOrListComponent;
}

export function getNonOverlappingSelectedSubConfigurations(
	rootItem: ConfiguratorTreeItemModel,
	selectedItems: ConfiguratorTreeItemModel[],
): ConfiguratorTreeItemModel[] {
	const selectedItemKeys = new Set(selectedItems.map((item) => item.key));
	const selectedRoots: ConfiguratorTreeItemModel[] = [];

	function collectSelectedRoots(item: ConfiguratorTreeItemModel): void {
		if (selectedItemKeys.has(item.key)) {
			selectedRoots.push(item);
			return;
		}

		item.children.forEach(collectSelectedRoots);
	}

	collectSelectedRoots(rootItem);
	return selectedRoots;
}

export function assignCompatibleSubConfiguratorComponents(
	targetComponents: TransformedSubConfiguratorOrListComponent[],
	sourceItems: ConfiguratorTreeItemModel[],
): SubConfigurationImportAssignment[] {
	const usedSingleComponents = new Set<TransformedSubConfiguratorOrListComponent>();
	const assignments: SubConfigurationImportAssignment[] = [];

	for (const sourceItem of sourceItems) {
		if (sourceItem.configuratorType.type !== "subConfigurator") continue;
		const sourceCatalogPartId = sourceItem.configuratorType.subConfiguratorCatalogPartId;

		const targetComponent = targetComponents.find(
			(component) =>
				component.catalogPartId === sourceCatalogPartId &&
				(component.type === "subConfiguratorList" || !usedSingleComponents.has(component)),
		);
		if (targetComponent == null) continue;

		assignments.push({ sourceItem: sourceItem, targetComponent: targetComponent });
		if (targetComponent.type === "subConfigurator") usedSingleComponents.add(targetComponent);
	}

	return assignments;
}

export function prepareImportedSubConfiguration({
	sourceItem,
	targetParent,
	targetComponent,
}: {
	sourceItem: ConfiguratorTreeItemModel;
	targetParent: ConfiguratorTreeItemModel;
	targetComponent: TransformedSubConfiguratorOrListComponent;
}): ConfiguratorTreeItemModel {
	return reparentImportedSubConfiguration({
		sourceItem: sourceItem,
		targetParent: targetParent,
		selfConfiguratorComponentName: targetComponent.name,
	});
}

export function synchronizeImportedSubConfigurationDescendantParentTypes(
	parentItem: ConfiguratorTreeItemModel,
): ConfiguratorTreeItemModel {
	return {
		...parentItem,
		children: parentItem.children.map((child) => {
			if (child.configuratorType.type !== "subConfigurator") return child;

			return synchronizeImportedSubConfigurationDescendantParentTypes({
				...child,
				configuratorType: {
					...child.configuratorType,
					parentConfiguratorType: parentItem.configuratorType,
					parentProperties: { props: [] },
				},
			});
		}),
	};
}

function reparentImportedSubConfiguration({
	sourceItem,
	targetParent,
	selfConfiguratorComponentName,
}: {
	sourceItem: ConfiguratorTreeItemModel;
	targetParent: ConfiguratorTreeItemModel;
	selfConfiguratorComponentName: string | null | undefined;
}): ConfiguratorTreeItemModel {
	if (sourceItem.configuratorType.type !== "subConfigurator") {
		throw new Error("Only subconfigurations can be imported");
	}

	const importedItem: ConfiguratorTreeItemModel = {
		...sourceItem,
		key: crypto.randomUUID(),
		selfConfiguratorComponentName: selfConfiguratorComponentName,
		configuratorType: {
			...sourceItem.configuratorType,
			parentConfiguratorType: targetParent.configuratorType,
			parentProperties: { props: [] },
		},
		children: [],
	};

	importedItem.children = sourceItem.children.map((child) =>
		reparentImportedSubConfiguration({
			sourceItem: child,
			targetParent: importedItem,
			selfConfiguratorComponentName: child.selfConfiguratorComponentName,
		}),
	);

	return importedItem;
}
